import { useEffect, useState } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIER_STYLE = { "Prioritize now": "bg-emerald-600 text-white", "Build toward": "bg-amber-500 text-white", "Long horizon": "bg-slate-200 text-slate-600" };

export default function RoadmapPage() {
  const { productId } = useProduct();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/portfolio-intelligence/${productId}`).then((r) => setData(r.data)).catch(console.error);
  }, [productId]);

  return (
    <PageShell pageId="roadmap" overline="Commercial & Portfolio Intelligence · Layer 11-12" title="Global Launch Strategy">
      {!data ? (
        <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Building roadmap…</div>
      ) : (
        <>
          <div data-testid="roadmap-kpis" className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white divide-x divide-y md:divide-y-0 divide-slate-200">
            {[["Portfolio Readiness", data.portfolio_readiness.score], ["Evidence Efficiency", data.evidence_efficiency.score],
              ["Global Coverage (weighted)", data.global_evidence_coverage.weighted_coverage_index],
              ["Top Access Opportunity", data.market_access_opportunity.top_market]].map(([l, v]) => (
              <div key={l} className="p-6"><p className="overline-label text-slate-400">{l}</p><p className="font-heading font-extrabold text-3xl mt-3">{v}</p></div>
            ))}
          </div>

          <section data-testid="expansion-sequence" className="bg-white border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <p className="overline-label text-slate-400">Country Expansion Priority</p>
              <h2 className="font-heading font-bold text-lg tracking-tight mt-1">Optimized Launch Sequence</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {data.country_expansion_priority.sequence.map((e, i) => (
                <div key={e.country} data-testid={`roadmap-${i}`} className="px-6 py-4 flex items-center gap-4">
                  <span className="font-mono-data text-xs text-slate-400 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-xl">{e.flag}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{e.country}
                      <span className={`ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${TIER_STYLE[e.tier]}`}>{e.tier}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{e.rationale}</p>
                  </div>
                  <div className="w-40 hidden md:block">
                    <div className="flex justify-between text-[10px] text-slate-400 mb-1"><span>Wave {e.wave}</span><span>~{e.months_to_decision} mo</span></div>
                    <div className="h-1.5 bg-slate-100"><div className="h-full bg-slate-900" style={{ width: `${e.expansion_priority}%` }} /></div>
                  </div>
                  <p className="font-heading font-extrabold text-xl w-10 text-right">{e.expansion_priority}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <section data-testid="investment-priorities" className="bg-white border border-slate-200 p-6">
              <p className="overline-label text-slate-400">Evidence Investment Priority</p>
              <div className="mt-4 space-y-4">
                {data.evidence_investment_priority.ranked_workstreams.map((w) => (
                  <div key={w.workstream}>
                    <div className="flex justify-between text-sm"><span className="font-semibold">{w.workstream}{w.country_specific && <span className="text-slate-400 font-normal"> (local)</span>}</span><span className="font-mono-data">{w.investment_priority}</span></div>
                    <div className="h-1.5 bg-slate-100 mt-1.5"><div className="h-full bg-amber-600" style={{ width: `${w.investment_priority}%` }} /></div>
                    <p className="text-xs text-slate-500 mt-1">{w.rationale}</p>
                  </div>
                ))}
              </div>
            </section>
            <section data-testid="coverage-panel" className="bg-white border border-slate-200 p-6">
              <p className="overline-label text-slate-400">Global Evidence Coverage</p>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">{data.global_evidence_coverage.rationale}</p>
              <div className="mt-4 space-y-3">
                {data.global_evidence_coverage.by_bucket.map((b) => (
                  <div key={b.bucket_id}>
                    <div className="flex justify-between text-xs"><span className="font-semibold text-slate-700">{b.bucket_id} · {b.name}</span><span className="font-mono-data">{b.coverage}%</span></div>
                    <div className="h-1.5 bg-slate-100 mt-1"><div className="h-full bg-blue-700" style={{ width: `${b.coverage}%` }} /></div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4">{data.evidence_efficiency.rationale}</p>
            </section>
          </div>
        </>
      )}
    </PageShell>
  );
}
